import type { AnyTokenSymbol } from '@incpt/shared';

// Shared display formatting for token amounts and USD values. Native coins
// need more fraction digits than stablecoins (BTC 8, ETH 6) — every screen must
// format through here instead of a local toFixed(2).
//
// PRECISION RULE: these helpers are for DISPLAY/gating only. UI→chain amount
// conversion must never go through Number arithmetic — use viem parseUnits
// (string → bigint) on the send path.

/** Keyed by Record so a new coin cannot be left out and silently fall back to
 *  2 digits — which would round a fee of 0.000084 BNB to "0". */
export const DISPLAY_DECIMALS: Record<AnyTokenSymbol, number> = {
  USDT: 2,
  USDC: 2,
  ETH: 6,
  BTC: 8,
  TRX: 6,
  BNB: 6,
  SOL: 6,
  HYPE: 6,
  // 9 decimals on chain, but the fee reserve is ~0.005 TON, so 6 digits show
  // it without the trailing noise of the full precision.
  TON: 6,
  ZEC: 8,
  // 12 on chain — more than any other coin here — but a Monero balance reads
  // naturally at 6, and the fee is around 0.00016 so it stays visible.
  XMR: 6,
  // 8 on chain like Bitcoin, and worth showing in full: a whole DOGE is worth
  // a few cents, so the interesting digits are the small ones — a fee of
  // 0.00226 would read as "0.00" at two.
  DOGE: 8,
  // 6 on chain, and all six matter: the fee is 10 drops (0.00001) and the
  // reserve is exactly 1, so a user comparing "balance" with "spendable"
  // needs to see the difference rather than two identical rounded numbers.
  XRP: 6,
};

export type DisplaySymbol = AnyTokenSymbol;

/** "12.5", "0.000021", "1,234.56" — trims trailing zeros, symbol-aware digits. */
export function formatTokenAmount(n: number, symbol: DisplaySymbol): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-US', {
    maximumFractionDigits: DISPLAY_DECIMALS[symbol] ?? 2,
  });
}

/** "1,234.56" — always two fraction digits. */
export function formatUsd(n: number): string {
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** "$40.00M", "$1.20K" — compact USD for statistics tiles. */
export function formatCompactUsd(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
}
